import * as Dialog from "@radix-ui/react-dialog";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Factory, PackageOpen, RefreshCw, FileText, ImageIcon, Lock, Paperclip, Plus, Search, Trash2, TriangleAlert, Upload, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router";
import { toast } from "sonner";
import { PageHeader, usePermission, useTenant } from "@/components/AppShell";
import { ShippingMethodSelect } from "@/components/ShippingMethodSelect";
import { Badge, Button, Card, cx, EmptyState, Input, Skeleton } from "@/components/ui";
import {
  api,
  ApiError,
  type Address,
  type ProductionBomDef,
  type ProductionOrderDetail,
  type ProductionOrderRow,
  type ProductionStatus,
  type ProductItem,
  type QuoteLine,
} from "@/lib/api";
import { fmtDateTime, fmtMoney, fmtNumber, fmtRelative } from "@/lib/format";
import { intlLocale, msg, t, tn } from "@/lib/i18n";

import { serverText } from "@/lib/server-text";
const TABS = [["orders", msg("Aufträge")], ["boms", msg("Stücklisten")], ["settings", msg("Einstellungen")]] as const;
type Tab = (typeof TABS)[number][0];
const STATUS: Record<ProductionStatus, { label: string; tone: "neutral" | "info" | "warning" | "success" | "danger" }> = {
  requested: { label: msg("Beauftragt"), tone: "info" },
  accepted: { label: msg("Angenommen"), tone: "info" },
  in_production: { label: msg("In Produktion"), tone: "warning" },
  completed: { label: msg("Fertig gemeldet"), tone: "success" },
  cancelled: { label: msg("Storniert"), tone: "neutral" },
  rejected: { label: msg("Abgelehnt"), tone: "danger" },
};
const SAMPLE: Record<string, string> = { pending: msg("Muster ausstehend"), submitted: msg("Muster zur Freigabe"), approved: msg("Muster freigegeben"), rejected: msg("Muster abgelehnt") };
const PRICE_MODES = [["none", msg("ohne Preis")], ["per_unit", msg("je Stück")], ["per_run", msg("pauschal je Auftrag")], ["per_minute", msg("je Minute")]] as const;

const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? t("Fehler")) : t("Fehler"));
const fmtDay = (d: string | null | undefined) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString(intlLocale()) : "—");
const toBase64 = (f: File) =>
  new Promise<string>((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1] ?? "");
    r.onerror = () => rej(r.error);
    r.readAsDataURL(f);
  });
const euros = (minor: number) => (minor / 100).toFixed(2).replace(".", ",");
const parseEuros = (s: string) => Math.round(Number(s.replace(/\./g, "").replace(",", ".")) * 100);

/**
 * Produktion (Konfektionierung durch Logiful): Kunden pflegen Produktionsstücklisten und beauftragen; Logiful nimmt an
 * (Termin, Preis), startet (Komponenten gehen beim Lager raus), lässt ggf. ein Muster freigeben und meldet fertig
 * (Wareneingang mit Charge/MHD).
 */
export function ProductionPage() {
  const [params, setParams] = useSearchParams();
  const { me } = useTenant();
  const may = usePermission();
  const tabs = TABS.filter(([k]) => k !== "settings" || me.user.isStaff);
  const tab = (tabs.find(([k]) => k === params.get("tab"))?.[0] ?? "orders") as Tab;
  if (!may("production", "view"))
    return (
      <div className="pb-16">
        <PageHeader title={t("Produktion")} />
        <EmptyState icon={<Factory className="size-5" />} title={t("Kein Zugriff")}>{t("Die Produktion sehen nur Personen mit dem Recht „Produktion“ – bitte an einen Administrator wenden.")}</EmptyState>
      </div>
    );
  return (
    <div className="pb-16">
      <PageHeader title={t("Produktion")} description={t("Logiful konfektioniert für euch: Stückliste mit Komponenten, Hilfsmaterial und Arbeitsschritten anlegen, Produktion beauftragen – die Fertigware kommt mit Charge und MHD auf Lager.")} />
      <div className="px-4 md:px-8 space-y-4">
        <div className="max-w-full overflow-x-auto">
          <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border" role="tablist">
            {tabs.map(([k, label]) => (
              <button key={k} role="tab" aria-selected={tab === k} onClick={() => setParams(k === "orders" ? {} : { tab: k }, { replace: true })} className={cx("h-8 px-4 rounded-md text-[13px] font-medium whitespace-nowrap", tab === k ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>
                {t(label)}
              </button>
            ))}
          </div>
        </div>
        {tab === "orders" && <OrdersPanel />}
        {tab === "boms" && <BomsPanel />}
        {tab === "settings" && <SettingsPanel />}
      </div>
    </div>
  );
}

// ── Aufträge ───────────────────────────────────────────────────────────

function OrdersPanel() {
  const { tenantId } = useTenant();
  const may = usePermission();
  const [params, setParams] = useSearchParams();
  const [filter, setFilter] = useState<"open" | "done" | "all">("open");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const q = useQuery({ queryKey: ["production-orders", tenantId, "all"], queryFn: () => api.production.orders(tenantId, "all"), placeholderData: keepPreviousData });
  const OPEN_STATUSES: ProductionStatus[] = ["requested", "accepted", "in_production"];
  const term = search.trim().toLowerCase();
  const matches = (o: ProductionOrderRow) => !term || `${o.number} ${o.product.sku} ${o.product.name} ${o.components?.orderNumber ?? ""} ${o.inbound?.number ?? ""}`.toLowerCase().includes(term);
  const found = (q.data?.items ?? []).filter(matches);
  const inFilter = (o: ProductionOrderRow, k: typeof filter) => k === "all" || (k === "open") === OPEN_STATUSES.includes(o.status);
  const items = found.filter((o) => inFilter(o, filter));
  const openId = params.get("open");
  const open = (id: string | null) => setParams((p) => { const n = new URLSearchParams(p); if (id) n.set("open", id); else n.delete("open"); return n; }, { replace: true });

  return (
    <>
      <div className="flex items-center gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Suchen: Auftragsnr., Fertigartikel, SKU, Lager-Auftrag, Wareneingang …")} aria-label={t("Produktionsaufträge durchsuchen")} leading={<Search className="size-4" />} suffix={search ? <button onClick={() => setSearch("")} className="size-6 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Suche leeren")}><X className="size-3.5" /></button> : undefined} className="h-11 text-[14px] flex-1" />
        {may("production") && <Button variant="primary" className="shrink-0" icon={<Plus className="size-4" />} onClick={() => setCreating(true)}>{t("Produktion beauftragen")}</Button>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex p-0.5 rounded-lg bg-surface-2 border border-border overflow-x-auto max-w-full">
          {([["open", msg("Offen")], ["done", msg("Abgeschlossen")], ["all", msg("Alle")]] as const).map(([k, l]) => (
            <button key={k} onClick={() => setFilter(k)} className={cx("h-7 px-3 rounded-md text-[12.5px] font-medium flex items-center gap-1.5 whitespace-nowrap", filter === k ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>
              {t(l)} <span className="tabular-nums text-fg-subtle">{q.data ? found.filter((o) => inFilter(o, k)).length : "·"}</span>
            </button>
          ))}
        </div>
      </div>
      <Card className="overflow-hidden">
        {!q.data ? (
          <Skeleton className="h-40" />
        ) : items.length === 0 ? (
          <EmptyState icon={term ? <Search className="size-5" /> : <Factory className="size-5" />} title={term ? t("Nichts gefunden") : filter === "open" ? t("Keine offenen Produktionsaufträge") : t("Keine Produktionsaufträge")}>
            {term ? t("Nichts zu „{q}“.", { q: search.trim() }) : t("Lege zuerst unter „Stücklisten“ fest, woraus ein Artikel besteht – dann kannst du die Produktion hier beauftragen.")}
          </EmptyState>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[12px] text-fg-muted border-b border-border">
                  <th className="px-4 py-2.5 font-medium">{t("Auftrag")}</th>
                  <th className="px-3 py-2.5 font-medium">{t("Fertigartikel")}</th>
                  <th className="px-3 py-2.5 font-medium text-right">{t("Menge")}</th>
                  <th className="px-3 py-2.5 font-medium">{t("Status")}</th>
                  <th className="px-3 py-2.5 font-medium">{t("Termin")}</th>
                  <th className="px-3 py-2.5 font-medium text-right">{t("Preis")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((o) => <OrderRow key={o.id} o={o} onOpen={() => open(o.id)} />)}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {creating && <NewOrderSheet onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); open(id); }} />}
      {openId && <OrderSheet id={openId} onClose={() => open(null)} />}
    </>
  );
}

function StatusBadge({ s }: { s: ProductionStatus }) {
  return <Badge tone={STATUS[s].tone}>{t(STATUS[s].label)}</Badge>;
}

function OrderRow({ o, onOpen }: { o: ProductionOrderRow; onOpen: () => void }) {
  const waiting = o.components?.holdReasons.find((r) => r.startsWith("Fehlbestand"));
  return (
    <tr onClick={onOpen} className="border-b border-border last:border-0 hover:bg-surface-2/60 cursor-pointer">
      <td className="px-4 py-2.5">
        <span className="font-medium whitespace-nowrap">{o.number}</span>
        <span className="block text-[11.5px] text-fg-subtle">{fmtRelative(o.createdAt)}</span>
      </td>
      <td className="px-3 py-2.5">
        <span className="block truncate max-w-72">{o.product.name}</span>
        <span className="font-mono text-[11.5px] text-fg-muted">{o.product.sku}</span>
      </td>
      <td className="px-3 py-2.5 text-right tabular-nums">{o.producedQuantity != null ? <>{fmtNumber(o.producedQuantity)} <span className="text-fg-subtle">/ {fmtNumber(o.quantity)}</span></> : fmtNumber(o.quantity)}</td>
      <td className="px-3 py-2.5">
        <div className="flex flex-wrap gap-1">
          <StatusBadge s={o.status} />
          {o.kind === "disassembly" && <Badge icon={<PackageOpen className="size-3" />}>{t("Auflösen")}</Badge>}
          {o.auto && <Badge icon={<RefreshCw className="size-3" />}>{t("automatisch")}</Badge>}
          {o.status === "in_production" && o.sampleStatus && o.sampleStatus !== "approved" && <Badge tone={o.sampleStatus === "submitted" ? "warning" : "neutral"}>{t(SAMPLE[o.sampleStatus]!)}</Badge>}
          {waiting && <Badge tone="danger">{t("Komponenten fehlen")}</Badge>}
        </div>
      </td>
      <td className="px-3 py-2.5 text-fg-muted">{o.plannedDate ? <>{fmtDay(o.plannedDate)} <span className="text-[11.5px]">{t("geplant")}</span></> : o.dueDate ? <>{fmtDay(o.dueDate)} <span className="text-[11.5px]">{t("Wunsch")}</span></> : "—"}</td>
      <td className="px-3 py-2.5 text-right tabular-nums">{o.quoteTotalMinor != null ? fmtMoney(o.quoteTotalMinor) : <span className="text-fg-subtle">{t("offen")}</span>}</td>
    </tr>
  );
}

/** Seitenpanel (Radix-Dialog) */
function Sheet({ title, description, onClose, children, footer, wide }: { title: ReactNode; description?: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/20 animate-fade-in" />
        <Dialog.Content onOpenAutoFocus={(e) => e.preventDefault()} className={cx("fixed z-50 right-0 top-0 h-full bg-surface border-l border-border shadow-2xl flex flex-col animate-sheet-in outline-none", wide ? "w-[min(820px,100vw)]" : "w-[min(680px,100vw)]")}>
          <div className="flex items-start gap-3 px-6 pt-5 pb-4 border-b border-border">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-[17px] font-semibold flex items-center gap-2">{title}</Dialog.Title>
              <Dialog.Description className="text-[13px] text-fg-muted mt-0.5">{description}</Dialog.Description>
            </div>
            <Dialog.Close className="size-8 -mr-2 grid place-items-center rounded-lg text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Schließen")}><X className="size-4" /></Dialog.Close>
          </div>
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">{children}</div>
          {footer && <div className="px-6 py-3 border-t border-border flex flex-wrap items-center gap-2">{footer}</div>}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Section({ title, children, aside }: { title: string; children: ReactNode; aside?: ReactNode }) {
  return (
    <section>
      <div className="flex items-baseline gap-2 mb-2.5">
        <h3 className="text-[12px] font-semibold text-fg-muted uppercase tracking-wide">{t(title)}</h3>
        {aside && <span className="ml-auto text-[12px] text-fg-muted">{aside}</span>}
      </div>
      {children}
    </section>
  );
}

function NewOrderSheet({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const boms = useQuery({ queryKey: ["production-boms", tenantId], queryFn: () => api.production.boms(tenantId) });
  const active = boms.data?.items.filter((b) => !b.archived) ?? [];
  const [productId, setProductId] = useState("");
  const [kind, setKind] = useState<"assembly" | "disassembly">("assembly");
  const [quantity, setQuantity] = useState("100");
  const [dueDate, setDueDate] = useState("");
  const [note, setNote] = useState("");
  const qty = Math.max(0, Math.floor(Number(quantity) || 0));
  const [debounced, setDebounced] = useState(qty);
  useEffect(() => { const h = setTimeout(() => setDebounced(qty), 350); return () => clearTimeout(h); }, [qty]);
  useEffect(() => { if (!productId && active[0]) setProductId(active[0].productId); }, [active, productId]);
  const pv = useQuery({ queryKey: ["production-preview", tenantId, productId, debounced, kind], queryFn: () => api.production.preview(tenantId, { productId, quantity: debounced, kind }), enabled: !!productId && debounced > 0, placeholderData: keepPreviousData });
  const create = useMutation({
    mutationFn: () => api.production.create(tenantId, { productId, quantity: qty, kind, dueDate: dueDate || null, ...(note.trim() ? { note: note.trim() } : {}) }),
    onSuccess: (r) => { toast.success(t("Produktion beauftragt"), { description: t("{n} – die Komponenten sind für euch reserviert. Logiful meldet sich mit Termin und Preis.", { n: r.number }) }); void qc.invalidateQueries({ queryKey: ["production-orders"] }); onCreated(r.id); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const missing = pv.data?.needs.filter((n) => n.missing > 0) ?? [];

  return (
    <Sheet title={<><Factory className="size-4 text-fg-subtle" /> {t("Produktion beauftragen")}</>} description={kind === "assembly" ? t("Die Komponenten werden sofort reserviert und nicht mehr an Shops verkauft. Logiful prüft, nennt Termin und Preis und startet die Produktion.") : t("Die Fertigartikel werden sofort reserviert und nicht mehr an Shops verkauft. Logiful prüft, nennt Termin und Preis und startet das Auflösen.")} onClose={onClose}
      footer={<><Dialog.Close asChild><Button variant="ghost">{t("Abbrechen")}</Button></Dialog.Close><Button variant="primary" className="ml-auto" disabled={!productId || qty < 1} loading={create.isPending} onClick={() => create.mutate()}>{t("Verbindlich beauftragen")}</Button></>}>
      {boms.data && active.length === 0 ? (
        <EmptyState icon={<FileText className="size-5" />} title={t("Noch keine Stückliste")}>{t("Lege zuerst unter „Stücklisten“ fest, woraus der Artikel besteht.")}</EmptyState>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2 inline-flex w-fit p-0.5 rounded-lg bg-surface-2 border border-border" role="radiogroup" aria-label={t("Art")}>
              {([["assembly", msg("Produzieren")], ["disassembly", msg("Auflösen")]] as const).map(([k, l]) => (
                <button key={k} role="radio" aria-checked={kind === k} onClick={() => setKind(k)} className={cx("h-8 px-4 rounded-md text-[13px] font-medium", kind === k ? "bg-surface shadow-sm" : "text-fg-muted hover:text-fg")}>{t(l)}</button>
              ))}
            </div>
            {kind === "disassembly" && <p className="sm:col-span-2 text-[12.5px] text-fg-muted">{t("Fertigartikel gehen zu Logiful und werden wieder in ihre Komponenten zerlegt – die Komponenten kommen per Wareneingang zurück auf Lager.")}</p>}
            <label className="sm:col-span-2 block">
              <span className="text-[12.5px] font-medium">{t("Fertigartikel")}</span>
              <select value={productId} onChange={(e) => setProductId(e.target.value)} className="mt-1 w-full h-9 rounded-lg border border-border bg-surface px-2.5 text-[13px]">
                {active.map((b) => <option key={b.productId} value={b.productId}>{b.sku} – {b.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-[12.5px] font-medium">{kind === "assembly" ? t("Menge (Fertigstücke)") : t("Menge (aufzulösende Stück)")}</span>
              <Input className="mt-1" inputMode="numeric" value={quantity} onChange={(e) => setQuantity(e.target.value.replace(/\D/g, ""))} />
            </label>
            <label className="block">
              <span className="text-[12.5px] font-medium">{t("Wunschtermin")} <span className="text-fg-subtle font-normal">({t("optional")})</span></span>
              <Input className="mt-1" type="date" value={dueDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setDueDate(e.target.value)} />
            </label>
            <label className="sm:col-span-2 block">
              <span className="text-[12.5px] font-medium">{t("Hinweis an Logiful")} <span className="text-fg-subtle font-normal">({t("optional")})</span></span>
              <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={2000} className="mt-1 w-full rounded-lg border border-border bg-surface px-2.5 py-2 text-[13px]" />
            </label>
          </div>
          {pv.data && (
            <>
              <Section title={kind === "assembly" ? t("Bedarf") : t("Geht zu Logiful")} aside={kind === "assembly" ? tn(pv.data.plannedMinutes, "ca. {n} Minute Arbeitszeit", "ca. {n} Minuten Arbeitszeit") : undefined}>
                <NeedsTable needs={pv.data.needs} showMissing />
                {missing.length > 0 && <p className="mt-2 text-[12.5px] text-warning flex gap-1.5"><TriangleAlert className="size-4 shrink-0" />{t("Nicht alles ist auf Lager. Ihr könnt trotzdem beauftragen – Logiful startet, sobald die Ware da ist (Wareneingang anmelden).")}</p>}
              </Section>
              {pv.data.yields.length > 0 && (
                <Section title={t("Kommt voraussichtlich zurück aufs Lager")}>
                  <NeedsTable needs={pv.data.yields} />
                  <p className="mt-1.5 text-[12px] text-fg-subtle">{t("Material wie Kartons oder Flyer ist meist nicht wiederverwendbar – Logiful meldet die tatsächlichen Mengen.")}</p>
                </Section>
              )}
              <Section title={t("Preisvorschlag")} aside={pv.data.quote.lines.length ? fmtMoney(pv.data.quote.totalMinor) : undefined}>
                {pv.data.quote.lines.length ? <QuoteTable lines={pv.data.quote.lines} /> : <p className="text-[13px] text-fg-muted">{t("Den Preis nennt Logiful bei der Annahme.")}</p>}
                <p className="mt-1.5 text-[12px] text-fg-subtle">{t("Unverbindlich – den endgültigen Preis bestätigt Logiful bei der Annahme.")}</p>
              </Section>
              {pv.data.sampleRequired && <p className="text-[12.5px] text-fg-muted flex gap-1.5"><ImageIcon className="size-4 shrink-0" />{t("Vor der Serie bekommt ihr ein Muster mit Fotos zur Freigabe.")}</p>}
            </>
          )}
        </>
      )}
    </Sheet>
  );
}

function NeedsTable({ needs, showMissing }: { needs: { productId: string; sku: string; name: string; role: "component" | "material"; quantity: number; available?: number; missing?: number }[]; showMissing?: boolean }) {
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <table className="w-full text-[13px]">
        <tbody>
          {needs.map((n) => (
            <tr key={n.productId} className="border-b border-border last:border-0">
              <td className="px-3 py-2">
                <span className="block truncate">{n.name}</span>
                <span className="font-mono text-[11.5px] text-fg-muted">{n.sku}</span>
                {n.role === "material" && <Badge className="ml-2">{t("Material")}</Badge>}
              </td>
              <td className="px-3 py-2 text-right tabular-nums font-medium">{fmtNumber(n.quantity)}</td>
              {n.available != null && <td className="px-3 py-2 text-right tabular-nums text-[12px] text-fg-muted w-36">{t("{n} verfügbar", { n: fmtNumber(n.available) })}</td>}
              {showMissing && <td className="px-3 py-2 text-right w-28">{(n.missing ?? 0) > 0 ? <Badge tone="danger">{t("fehlt {n}", { n: fmtNumber(n.missing!) })}</Badge> : <CheckCircle2 className="size-4 text-success inline" />}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function QuoteTable({ lines }: { lines: QuoteLine[] }) {
  return (
    <div className="rounded-xl border border-border overflow-hidden">
      <table className="w-full text-[13px]">
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-b border-border last:border-0">
              <td className="px-3 py-2">{t(l.label)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-fg-muted whitespace-nowrap">{fmtNumber(l.quantity)} {l.unit} × {fmtMoney(l.unitPriceMinor)}</td>
              <td className="px-3 py-2 text-right tabular-nums font-medium w-28">{fmtMoney(Math.round(l.quantity * l.unitPriceMinor))}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Angebot frei bearbeiten (nur Logiful): Positionen, Menge, Einheit, Einzelpreis */
function QuoteEditor({ lines, onChange }: { lines: QuoteLine[]; onChange: (l: QuoteLine[]) => void }) {
  const set = (i: number, p: Partial<QuoteLine>) => onChange(lines.map((l, j) => (j === i ? { ...l, ...p } : l)));
  return (
    <div className="space-y-1.5">
      {lines.map((l, i) => (
        <div key={i} className="flex flex-wrap items-center gap-1.5">
          <Input className="flex-1 min-w-40" value={l.label} onChange={(e) => set(i, { label: e.target.value })} aria-label={t("Position")} />
          <Input className="w-20 text-right" value={String(l.quantity).replace(".", ",")} onChange={(e) => set(i, { quantity: Number(e.target.value.replace(",", ".")) || 0 })} aria-label={t("Menge")} />
          <Input className="w-20" value={l.unit} onChange={(e) => set(i, { unit: e.target.value })} aria-label={t("Einheit")} />
          <Input className="w-24 text-right" defaultValue={euros(l.unitPriceMinor)} onBlur={(e) => set(i, { unitPriceMinor: parseEuros(e.target.value) || 0 })} aria-label={t("Einzelpreis in Euro")} suffix="€" />
          <button onClick={() => onChange(lines.filter((_, j) => j !== i))} className="size-8 grid place-items-center rounded-lg text-fg-subtle hover:text-danger" aria-label={t("Position entfernen")}><Trash2 className="size-3.5" /></button>
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => onChange([...lines, { label: t("Sonstiges"), quantity: 1, unit: t("pauschal"), unitPriceMinor: 0 }])}>{t("Position")}</Button>
        <span className="ml-auto text-[13px] font-semibold tabular-nums">{fmtMoney(lines.reduce((s, l) => s + Math.round(l.quantity * l.unitPriceMinor), 0))}</span>
      </div>
    </div>
  );
}

function FileList({ files, canDelete, onDeleted }: { files: { id: string; filename: string; mimeType: string; size: number }[]; canDelete?: boolean; onDeleted?: () => void }) {
  const { tenantId } = useTenant();
  if (!files.length) return null;
  return (
    <ul className="flex flex-wrap gap-2">
      {files.map((f) => (
        <li key={f.id} className="inline-flex items-center gap-1.5 h-8 pl-2.5 pr-1 rounded-lg border border-border bg-surface text-[12.5px]">
          {f.mimeType.startsWith("image/") ? <ImageIcon className="size-3.5 text-fg-subtle" /> : <FileText className="size-3.5 text-fg-subtle" />}
          <a href={api.production.fileUrl(tenantId, f.id)} target="_blank" rel="noreferrer" className="hover:underline max-w-48 truncate">{f.filename}</a>
          {canDelete && (
            <button onClick={() => void api.production.deleteFile(tenantId, f.id).then(() => onDeleted?.(), (e) => toast.error(errMsg(e)))} className="size-6 grid place-items-center rounded-md text-fg-subtle hover:text-danger" aria-label={t("Datei entfernen")}><X className="size-3" /></button>
          )}
        </li>
      ))}
    </ul>
  );
}

function UploadButton({ label, onFile, accept = "application/pdf,image/*" }: { label: string; onFile: (f: { filename: string; data: string }) => Promise<unknown>; accept?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (f.size > 15 * 1024 * 1024) return toast.error(t("Datei zu groß (höchstens 15 MB)."));
        setBusy(true);
        try { await onFile({ filename: f.name, data: await toBase64(f) }); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
      }} />
      <Button size="sm" variant="secondary" icon={<Upload className="size-3.5" />} loading={busy} onClick={() => ref.current?.click()}>{t(label)}</Button>
    </>
  );
}

function OrderSheet({ id, onClose }: { id: string; onClose: () => void }) {
  const { tenantId, me } = useTenant();
  const qc = useQueryClient();
  const staff = me.user.isStaff;
  const q = useQuery({ queryKey: ["production-order", tenantId, id], queryFn: () => api.production.order(tenantId, id) });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["production-order", tenantId, id] }); void qc.invalidateQueries({ queryKey: ["production-orders"] }); };
  const run = useMutation({ mutationFn: (f: () => Promise<unknown>) => f(), onSuccess: refresh, onError: (e) => toast.error(errMsg(e)) });
  const o = q.data;
  return (
    <Sheet wide onClose={onClose}
      title={o ? <>{o.number} <StatusBadge s={o.status} />{o.kind === "disassembly" && <Badge icon={<PackageOpen className="size-3" />}>{t("Auflösen")}</Badge>}{o.auto && <Badge icon={<RefreshCw className="size-3" />}>{t("automatisch")}</Badge>}{o.billed && <Badge tone="success">{t("abgerechnet")}</Badge>}</> : t("Produktionsauftrag")}
      description={o ? <>{fmtNumber(o.quantity)} × {o.product.name} <span className="font-mono text-[12px]">({o.product.sku})</span> · {t("Stückliste Version {v}", { v: o.bomVersion })}</> : undefined}
      footer={o && <OrderActions o={o} run={run.mutate} busy={run.isPending} />}>
      {!o ? <Skeleton className="h-80" /> : <OrderBody o={o} staff={staff} run={run.mutate} refresh={refresh} />}
    </Sheet>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[12px] text-fg-muted">{t(label)}</dt>
      <dd className="text-[13px] font-medium mt-0.5">{children}</dd>
    </div>
  );
}

function OrderBody({ o, staff, run, refresh }: { o: ProductionOrderDetail; staff: boolean; run: (f: () => Promise<unknown>) => void; refresh: () => void }) {
  const { tenantId } = useTenant();
  const [feedback, setFeedback] = useState("");
  const [sampleNote, setSampleNote] = useState(o.sampleNote ?? "");
  const [editQuote, setEditQuote] = useState<QuoteLine[] | null>(null);
  const shortage = o.componentsOrder?.holdReasons.filter((r) => r.startsWith("Fehlbestand")) ?? [];
  const samples = o.files.filter((f) => f.kind === "sample");
  const instructions = o.files.filter((f) => f.kind === "instruction");
  const quote = o.quote ?? null;
  return (
    <>
      {o.cancelReason && <p className="rounded-xl border border-border bg-surface-2/60 p-3 text-[13px]">{o.status === "rejected" ? t("Abgelehnt von Logiful:") : t("Storniert:")} {o.cancelReason}</p>}
      <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Field label={t("Wunschtermin")}>{fmtDay(o.dueDate)}</Field>
        <Field label={t("Geplant")}>{staff && !["completed", "cancelled", "rejected"].includes(o.status) ? <input type="date" defaultValue={o.plannedDate ?? ""} onBlur={(e) => e.target.value !== (o.plannedDate ?? "") && run(() => api.production.update(tenantId, o.id, { plannedDate: e.target.value || null }))} className="h-7 rounded-md border border-border bg-surface px-1.5 text-[12.5px]" /> : fmtDay(o.plannedDate)}</Field>
        <Field label={t("Arbeitszeit (Plan)")}>{tn(o.plannedMinutes, "{n} Minute", "{n} Minuten")}</Field>
        <Field label={t("Preis")}>{o.quoteTotalMinor != null ? fmtMoney(o.quoteTotalMinor) : t("offen")}</Field>
      </dl>
      {o.note && <p className="text-[13px]"><span className="text-fg-muted">{t("Hinweis vom Kunden:")}</span> {o.note}</p>}
      {staff && (
        <label className="block">
          <span className="text-[12px] text-fg-muted flex items-center gap-1"><Lock className="size-3" /> {t("Interne Notiz (nur Logiful)")}</span>
          <textarea defaultValue={o.internalNote ?? ""} rows={2} onBlur={(e) => e.target.value !== (o.internalNote ?? "") && run(() => api.production.update(tenantId, o.id, { internalNote: e.target.value || null }))} className="mt-1 w-full rounded-lg border border-border bg-surface px-2.5 py-2 text-[13px]" />
        </label>
      )}

      <Section title={o.kind === "assembly" ? t("Komponenten & Material") : t("Geht zu Logiful")} aside={o.componentsOrder ? <Link to={`/orders?open=${o.componentsOrder.id}`} className="hover:underline">{t("Lager-Auftrag {n}", { n: o.componentsOrder.orderNumber })}</Link> : undefined}>
        <NeedsTable needs={o.needs} />
        {shortage.length > 0 && <p className="mt-2 text-[12.5px] text-danger flex gap-1.5"><TriangleAlert className="size-4 shrink-0" />{shortage.join(" · ")}</p>}
        {o.status === "requested" || o.status === "accepted" ? <p className="mt-1.5 text-[12px] text-fg-subtle">{t("Reserviert – geht ans Lager, sobald Logiful die Produktion startet.")}</p> : null}
        {o.status === "in_production" && o.componentsOrder && <p className="mt-1.5 text-[12px] text-fg-subtle">{["shipped", "partially_shipped"].includes(o.componentsOrder.status) ? t("Komponenten sind beim Produktionsplatz.") : t("Komponenten sind beim Lager angefordert.")}</p>}
      </Section>

      {o.yields.length > 0 && (
        <Section title={t("Kommt zurück aufs Lager")}>
          <NeedsTable needs={o.yields} />
        </Section>
      )}

      {o.kind === "assembly" && (o.bom.steps.length > 0 || o.bom.logifulMaterials.length > 0 || instructions.length > 0) && (
        <Section title={t("Arbeitsschritte")}>
          <ol className="space-y-1.5 text-[13px] list-decimal pl-5">
            {o.bom.steps.map((s) => <li key={s.id}><span className="font-medium">{t(s.title)}</span>{s.instructions && <span className="block text-fg-muted text-[12.5px] whitespace-pre-line">{s.instructions}</span>}</li>)}
          </ol>
          {o.bom.logifulMaterials.length > 0 && <p className="mt-2 text-[12.5px] text-fg-muted">{t("Material von Logiful:")} {o.bom.logifulMaterials.map((m) => `${m.name} (${fmtNumber(m.quantity)} ${m.unit} ${t("je Stück")})`).join(", ")}</p>}
          {o.bom.note && <p className="mt-1 text-[12.5px] text-fg-muted">{o.bom.note}</p>}
          <div className="mt-2"><FileList files={instructions} /></div>
        </Section>
      )}

      <Section title={t("Preis")} aside={staff && o.can.editQuote && !editQuote && !o.can.accept ? <button onClick={() => setEditQuote((quote ?? o.estimate).lines)} className="hover:underline">{t("bearbeiten")}</button> : undefined}>
        {editQuote ? (
          <>
            <QuoteEditor lines={editQuote} onChange={setEditQuote} />
            <div className="mt-2 flex justify-end gap-2">
              <Button size="sm" variant="ghost" onClick={() => setEditQuote(null)}>{t("Verwerfen")}</Button>
              <Button size="sm" variant="primary" onClick={() => { run(() => api.production.saveQuote(tenantId, o.id, { lines: editQuote })); setEditQuote(null); }}>{t("Preis speichern")}</Button>
            </div>
          </>
        ) : quote ? (
          <>{quote.lines.length ? <QuoteTable lines={quote.lines} /> : <p className="text-[13px] text-fg-muted">{t("Ohne Berechnung")}</p>}{quote.note && <p className="mt-1.5 text-[12.5px] text-fg-muted">{quote.note}</p>}</>
        ) : (
          <>{o.estimate.lines.length ? <QuoteTable lines={o.estimate.lines} /> : null}<p className="mt-1.5 text-[12px] text-fg-subtle">{t("Vorschlag aus der Stückliste – Logiful bestätigt den Preis bei der Annahme.")}</p></>
        )}
      </Section>

      {o.can.accept && <AcceptBox o={o} run={run} />}

      {o.sampleRequired && ["in_production", "completed"].includes(o.status) && (
        <Section title={t("Muster")} aside={o.sampleStatus ? t(SAMPLE[o.sampleStatus]!) : undefined}>
          <div className="space-y-2">
            <FileList files={samples} canDelete={staff && o.sampleStatus !== "approved"} onDeleted={refresh} />
            {o.sampleNote && <p className="text-[13px]"><span className="text-fg-muted">{t("Logiful:")}</span> {o.sampleNote}</p>}
            {o.sampleFeedback && <p className="text-[13px]"><span className="text-fg-muted">{t("Rückmeldung:")}</span> {o.sampleFeedback}</p>}
            {o.can.sample && (
              <div className="rounded-xl border border-border p-3 space-y-2">
                <UploadButton label={t("Musterfoto hochladen")} accept="image/*" onFile={async (f) => { await api.production.uploadSample(tenantId, o.id, f); refresh(); }} />
                <textarea value={sampleNote} onChange={(e) => setSampleNote(e.target.value)} rows={2} placeholder={t("Hinweis zum Muster (optional)")} className="w-full rounded-lg border border-border bg-surface px-2.5 py-2 text-[13px]" />
                <Button size="sm" variant="primary" disabled={!samples.length} onClick={() => run(() => api.production.submitSample(tenantId, o.id, sampleNote.trim() || undefined))}>{o.sampleStatus === "rejected" ? t("Neues Muster zur Freigabe geben") : t("Zur Freigabe an den Kunden")}</Button>
              </div>
            )}
            {o.can.decideSample && (
              <div className="rounded-xl border border-warning/40 bg-warning/5 p-3 space-y-2">
                <p className="text-[13px] font-medium">{t("Passt das Muster? Erst nach eurer Freigabe produziert Logiful die Serie.")}</p>
                <textarea value={feedback} onChange={(e) => setFeedback(e.target.value)} rows={2} placeholder={t("Was soll anders werden? (bei Ablehnung Pflicht)")} className="w-full rounded-lg border border-border bg-surface px-2.5 py-2 text-[13px]" />
                <div className="flex gap-2">
                  <Button size="sm" variant="primary" onClick={() => run(() => api.production.decideSample(tenantId, o.id, { approved: true, ...(feedback.trim() ? { feedback: feedback.trim() } : {}) }))}>{t("Muster freigeben")}</Button>
                  <Button size="sm" variant="secondary" disabled={!feedback.trim()} onClick={() => run(() => api.production.decideSample(tenantId, o.id, { approved: false, feedback: feedback.trim() }))}>{t("Ablehnen")}</Button>
                </div>
              </div>
            )}
          </div>
        </Section>
      )}

      {o.can.complete && (o.kind === "assembly" ? <CompleteBox o={o} run={run} /> : <DisassembleBox o={o} run={run} />)}

      {o.status === "completed" && (
        <Section title={t("Ergebnis")}>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Field label={o.kind === "assembly" ? t("Gutmenge") : t("Aufgelöst")}>{fmtNumber(o.producedQuantity)}</Field>
            <Field label={t("Ausschuss")}>{o.scrapQuantity ? `${fmtNumber(o.scrapQuantity)}${o.scrapReason ? ` – ${o.scrapReason}` : ""}` : "—"}</Field>
            {o.kind === "assembly" && <Field label={t("Charge / MHD")}>{[o.batch, o.bestBefore ? fmtDay(o.bestBefore) : null].filter(Boolean).join(" · ") || "—"}</Field>}
            <Field label={t("Wareneingang")}>{o.inbound ? <Link to={`/inbounds?open=${o.inbound.id}`} className="hover:underline">{o.inbound.number}</Link> : "—"}{o.inbound && <span className="block text-[12px] font-normal text-fg-muted">{t("{a} von {b} eingebucht", { a: fmtNumber(o.inbound.received), b: fmtNumber(o.inbound.expected) })}</span>}</Field>
          </dl>
        </Section>
      )}

      <Section title={t("Verlauf")}>
        <ul className="space-y-2">
          {o.events.map((e, i) => (
            <li key={i} className="text-[12.5px]">
              <span className="text-fg-muted tabular-nums">{fmtDateTime(e.at)}</span> {e.actor && <span className="text-fg-subtle">· {e.actor}</span>}
              <span className="block">{serverText(e.message)}</span>
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}

function AcceptBox({ o, run }: { o: ProductionOrderDetail; run: (f: () => Promise<unknown>) => void }) {
  const { tenantId } = useTenant();
  const [lines, setLines] = useState<QuoteLine[]>(o.estimate.lines);
  const [planned, setPlanned] = useState(o.plannedDate ?? o.dueDate ?? "");
  const [reason, setReason] = useState("");
  return (
    <div className="rounded-xl border border-border bg-surface-2/40 p-4 space-y-3">
      <p className="font-medium text-[13.5px] flex items-center gap-1.5"><Lock className="size-3.5 text-fg-subtle" /> {t("Annehmen (Logiful)")}</p>
      <label className="block max-w-56">
        <span className="text-[12.5px] font-medium">{t("Geplanter Termin")}</span>
        <Input className="mt-1" type="date" value={planned} onChange={(e) => setPlanned(e.target.value)} />
      </label>
      <div>
        <span className="text-[12.5px] font-medium">{t("Angebot")}</span>
        <div className="mt-1"><QuoteEditor lines={lines} onChange={setLines} /></div>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="primary" onClick={() => run(() => api.production.accept(tenantId, o.id, { plannedDate: planned || null, quote: { lines } }))}>{t("Annehmen")}</Button>
        <Input className="flex-1 min-w-48" value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("Grund für eine Ablehnung …")} />
        <Button variant="secondary" disabled={reason.trim().length < 3} onClick={() => run(() => api.production.reject(tenantId, o.id, reason.trim()))}>{t("Ablehnen")}</Button>
      </div>
    </div>
  );
}

function CompleteBox({ o, run }: { o: ProductionOrderDetail; run: (f: () => Promise<unknown>) => void }) {
  const { tenantId } = useTenant();
  const [produced, setProduced] = useState(String(o.quantity));
  const [scrap, setScrap] = useState("0");
  const [scrapReason, setScrapReason] = useState("");
  const [batch, setBatch] = useState(o.number);
  const [bestBefore, setBestBefore] = useState(o.suggestedBestBefore ?? "");
  const [minutes, setMinutes] = useState("");
  const scrapN = Number(scrap) || 0;
  return (
    <div className="rounded-xl border border-border bg-surface-2/40 p-4 space-y-3">
      <p className="font-medium text-[13.5px] flex items-center gap-1.5"><Lock className="size-3.5 text-fg-subtle" /> {t("Fertig melden (Logiful)")}</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block"><span className="text-[12.5px] font-medium">{t("Gutmenge")}</span><Input className="mt-1" inputMode="numeric" value={produced} onChange={(e) => setProduced(e.target.value.replace(/\D/g, ""))} /></label>
        <label className="block"><span className="text-[12.5px] font-medium">{t("Ausschuss")}</span><Input className="mt-1" inputMode="numeric" value={scrap} onChange={(e) => setScrap(e.target.value.replace(/\D/g, ""))} /></label>
        <label className="block"><span className="text-[12.5px] font-medium">{t("Arbeitszeit (Min.)")}</span><Input className="mt-1" inputMode="numeric" value={minutes} placeholder={String(o.plannedMinutes)} onChange={(e) => setMinutes(e.target.value.replace(/\D/g, ""))} /></label>
        {scrapN > 0 && <label className="block sm:col-span-3"><span className="text-[12.5px] font-medium">{t("Grund für den Ausschuss")}</span><Input className="mt-1" value={scrapReason} onChange={(e) => setScrapReason(e.target.value)} /></label>}
        <label className="block"><span className="text-[12.5px] font-medium">{t("Charge")}{o.product.batchManaged && " *"}</span><Input className="mt-1" value={batch} onChange={(e) => setBatch(e.target.value)} /></label>
        <label className="block"><span className="text-[12.5px] font-medium">{t("MHD")}{o.product.bestBeforeManaged && " *"}</span><Input className="mt-1" type="date" value={bestBefore} onChange={(e) => setBestBefore(e.target.value)} /></label>
      </div>
      {o.suggestedBestBefore && <p className="text-[12px] text-fg-subtle">{t("Vorschlag: frühestes MHD der Komponenten ({d}).", { d: fmtDay(o.suggestedBestBefore) })}</p>}
      <Button variant="primary" onClick={() => run(() => api.production.complete(tenantId, o.id, { producedQuantity: Number(produced) || 0, scrapQuantity: scrapN, ...(scrapReason.trim() ? { scrapReason: scrapReason.trim() } : {}), ...(batch.trim() ? { batch: batch.trim() } : {}), ...(bestBefore ? { bestBefore } : {}), ...(minutes ? { actualMinutes: Number(minutes) } : {}) }))}>{t("Fertig melden und Wareneingang anlegen")}</Button>
    </div>
  );
}

function DisassembleBox({ o, run }: { o: ProductionOrderDetail; run: (f: () => Promise<unknown>) => void }) {
  const { tenantId } = useTenant();
  const [done, setDone] = useState(String(o.quantity));
  const [scrap, setScrap] = useState("0");
  const [scrapReason, setScrapReason] = useState("");
  const [lines, setLines] = useState(o.yields.map((y) => ({ productId: y.productId, sku: y.sku, name: y.name, quantity: String(y.quantity), batch: "", bestBefore: "" })));
  const set = (i: number, p: Partial<(typeof lines)[number]>) => setLines(lines.map((l, j) => (j === i ? { ...l, ...p } : l)));
  const scrapN = Number(scrap) || 0;
  return (
    <div className="rounded-xl border border-border bg-surface-2/40 p-4 space-y-3">
      <p className="font-medium text-[13.5px] flex items-center gap-1.5"><Lock className="size-3.5 text-fg-subtle" /> {t("Auflösung fertig melden (Logiful)")}</p>
      <div className="grid sm:grid-cols-3 gap-3">
        <label className="block"><span className="text-[12.5px] font-medium">{t("Aufgelöst (Stück)")}</span><Input className="mt-1" inputMode="numeric" value={done} onChange={(e) => setDone(e.target.value.replace(/\D/g, ""))} /></label>
        <label className="block"><span className="text-[12.5px] font-medium">{t("Ausschuss")}</span><Input className="mt-1" inputMode="numeric" value={scrap} onChange={(e) => setScrap(e.target.value.replace(/\D/g, ""))} /></label>
        {scrapN > 0 && <label className="block"><span className="text-[12.5px] font-medium">{t("Grund für den Ausschuss")}</span><Input className="mt-1" value={scrapReason} onChange={(e) => setScrapReason(e.target.value)} /></label>}
      </div>
      <div>
        <span className="text-[12.5px] font-medium">{t("Zurück aufs Lager")}</span>
        <div className="mt-1 space-y-1.5">
          {lines.map((l, i) => (
            <div key={l.productId} className="flex flex-wrap items-center gap-1.5">
              <span className="flex-1 min-w-40 text-[13px] truncate">{l.name} <span className="font-mono text-[11.5px] text-fg-muted">{l.sku}</span></span>
              <Input className="w-20 text-right" inputMode="numeric" value={l.quantity} onChange={(e) => set(i, { quantity: e.target.value.replace(/\D/g, "") })} aria-label={t("Menge")} />
              <Input className="w-28" value={l.batch} placeholder={t("Charge")} onChange={(e) => set(i, { batch: e.target.value })} aria-label={t("Charge")} />
              <Input className="w-36" type="date" value={l.bestBefore} onChange={(e) => set(i, { bestBefore: e.target.value })} aria-label={t("MHD")} />
            </div>
          ))}
        </div>
        <p className="mt-1.5 text-[12px] text-fg-subtle">{t("Charge und MHD nur angeben, wenn bekannt (Pflicht bei chargen- bzw. MHD-geführten Artikeln).")}</p>
      </div>
      <Button variant="primary" onClick={() => run(() => api.production.complete(tenantId, o.id, { producedQuantity: Number(done) || 0, scrapQuantity: scrapN, ...(scrapReason.trim() ? { scrapReason: scrapReason.trim() } : {}), lines: lines.map((l) => ({ productId: l.productId, quantity: Number(l.quantity) || 0, ...(l.batch.trim() ? { batch: l.batch.trim() } : {}), ...(l.bestBefore ? { bestBefore: l.bestBefore } : {}) })) }))}>{t("Fertig melden und Wareneingang anlegen")}</Button>
    </div>
  );
}

function OrderActions({ o, run, busy }: { o: ProductionOrderDetail; run: (f: () => Promise<unknown>) => void; busy: boolean }) {
  const { tenantId } = useTenant();
  const settings = useQuery({ queryKey: ["production-settings", tenantId], queryFn: () => api.production.settings(tenantId), enabled: o.can.start });
  const [method, setMethod] = useState<string | undefined>(undefined);
  const [confirmCancel, setConfirmCancel] = useState(false);
  return (
    <>
      {o.can.cancel && (confirmCancel ? (
        <>
          <span className="text-[13px]">{t("Wirklich stornieren? Die Reservierung wird aufgehoben.")}</span>
          <Button size="sm" variant="secondary" loading={busy} onClick={() => run(() => api.production.cancel(tenantId, o.id))}>{t("Ja, stornieren")}</Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmCancel(false)}>{t("Nein")}</Button>
        </>
      ) : <Button size="sm" variant="ghost" onClick={() => setConfirmCancel(true)}>{t("Stornieren")}</Button>)}
      {o.can.start && (
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {!settings.data?.address && settings.data && <span className="text-[12.5px] text-danger">{t("Adresse „Logiful Produktion“ fehlt (Einstellungen)")}</span>}
          <ShippingMethodSelect value={method ?? settings.data?.shippingMethodId} onChange={setMethod} emptyLabel={t("Versandart: Lager-Standard")} className="w-56" />
          <Button variant="primary" loading={busy} disabled={!settings.data?.address} onClick={() => run(() => api.production.start(tenantId, o.id, method ?? settings.data?.shippingMethodId ?? null))}>{t("Produktion starten")}</Button>
        </div>
      )}
      {o.can.bill && <Button className="ml-auto" size="sm" variant="secondary" onClick={() => run(() => api.production.billed(tenantId, o.id, !o.billed))}>{o.billed ? t("Abrechnung zurücknehmen") : t("Als abgerechnet markieren")}</Button>}
    </>
  );
}

// ── Stücklisten ────────────────────────────────────────────────────────

function BomsPanel() {
  const { tenantId } = useTenant();
  const may = usePermission();
  const q = useQuery({ queryKey: ["production-boms", tenantId], queryFn: () => api.production.boms(tenantId) });
  const [editing, setEditing] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState("");
  const term = search.trim().toLowerCase();
  const boms = (q.data?.items ?? []).filter((b) => !term || `${b.sku} ${b.name}`.toLowerCase().includes(term));
  return (
    <>
      <div className="flex items-center gap-2">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("Stücklisten suchen: SKU oder Fertigartikel …")} aria-label={t("Stücklisten durchsuchen")} leading={<Search className="size-4" />} suffix={search ? <button onClick={() => setSearch("")} className="size-6 grid place-items-center rounded-md text-fg-subtle hover:bg-surface-2 hover:text-fg" aria-label={t("Suche leeren")}><X className="size-3.5" /></button> : undefined} className="h-11 text-[14px] flex-1" />
        {may("production") && <Button variant="primary" className="shrink-0" icon={<Plus className="size-4" />} onClick={() => setPicking(true)}>{t("Stückliste anlegen")}</Button>}
      </div>
      <p className="text-[12.5px] text-fg-muted">{t("Woraus ein Fertigartikel besteht: Komponenten, euer Material (Kartons, Flyer …), Material von Logiful und die Arbeitsschritte.")}</p>
      {picking && <ProductPicker onPick={(p) => { setPicking(false); setEditing(p.id); }} onClose={() => setPicking(false)} />}
      <Card className="overflow-hidden">
        {!q.data ? <Skeleton className="h-32" /> : boms.length === 0 ? (
          <EmptyState icon={<FileText className="size-5" />} title={t("Noch keine Produktionsstückliste")}>{t("Lege fest, aus welchen Artikeln und Arbeitsschritten ein Fertigartikel entsteht.")}</EmptyState>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-left text-[12px] text-fg-muted border-b border-border">
                <th className="px-4 py-2.5 font-medium">{t("Fertigartikel")}</th>
                <th className="px-3 py-2.5 font-medium text-right">{t("Komponenten")}</th>
                <th className="px-3 py-2.5 font-medium text-right">{t("Material")}</th>
                <th className="px-3 py-2.5 font-medium text-right">{t("Schritte")}</th>
                <th className="px-3 py-2.5 font-medium">{t("Stand")}</th>
              </tr>
            </thead>
            <tbody>
              {boms.map((b) => (
                <tr key={b.productId} onClick={() => setEditing(b.productId)} className={cx("border-b border-border last:border-0 hover:bg-surface-2/60 cursor-pointer", b.archived && "opacity-60")}>
                  <td className="px-4 py-2.5"><span className="block">{b.name}</span><span className="font-mono text-[11.5px] text-fg-muted">{b.sku}</span>{b.archived && <Badge className="ml-2">{t("archiviert")}</Badge>}{b.sampleRequired && <Badge className="ml-2">{t("mit Muster")}</Badge>}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{b.components}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{b.materials}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{b.steps}</td>
                  <td className="px-3 py-2.5 text-fg-muted">{t("Version {v}", { v: b.version })} · {fmtRelative(b.updatedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
      {editing && <BomSheet productId={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function useProductSearch(search: string) {
  const { tenantId } = useTenant();
  return useQuery({ queryKey: ["production-product-search", tenantId, search], queryFn: () => api.products.list(tenantId, { search, pageSize: 8, status: "active" }), enabled: search.trim().length >= 2 });
}

function ProductSearch({ placeholder, onPick, exclude = [] }: { placeholder: string; onPick: (p: ProductItem) => void; exclude?: string[] }) {
  const [search, setSearch] = useState("");
  const results = useProductSearch(search);
  const items = results.data?.items.filter((p) => !exclude.includes(p.id)) ?? [];
  return (
    <div className="relative">
      <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={placeholder} leading={<Search className="size-3.5" />} />
      {results.data && search.trim().length >= 2 && (
        <div className="absolute z-10 mt-1 w-full rounded-xl border border-border bg-surface shadow-xl p-1 animate-pop-in">
          {items.length === 0 && <div className="px-3 py-2 text-fg-muted text-[13px]">{t("Kein Artikel gefunden")}</div>}
          {items.map((p) => (
            <button key={p.id} onClick={() => { onPick(p); setSearch(""); }} className="w-full flex items-center gap-2 h-9 px-2.5 rounded-lg hover:bg-surface-2 text-left text-[13px]">
              <span className="font-mono text-[12px] text-fg-muted w-32 truncate">{p.sku}</span>
              <span className="truncate flex-1">{p.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductPicker({ onPick, onClose }: { onPick: (p: ProductItem) => void; onClose: () => void }) {
  return (
    <Card className="p-4 space-y-2">
      <div className="flex items-center">
        <p className="text-[13px] font-medium">{t("Für welchen Fertigartikel?")}</p>
        <button onClick={onClose} className="ml-auto size-7 grid place-items-center rounded-lg text-fg-subtle hover:bg-surface-2" aria-label={t("Schließen")}><X className="size-4" /></button>
      </div>
      <ProductSearch placeholder={t("Artikel suchen (SKU, Name, EAN) …")} onPick={onPick} />
      <p className="text-[12px] text-fg-subtle">{t("Den Fertigartikel bitte vorher wie jeden Artikel anlegen – er bekommt durch die Produktion eigenen Bestand.")}</p>
    </Card>
  );
}

const EMPTY_BOM: ProductionBomDef = { outputQuantity: 1, components: [], logifulMaterials: [], steps: [], sampleRequired: false, note: "", replenish: { enabled: false, minStock: 100, batchQuantity: 100, autoAccept: false } };

function BomSheet({ productId, onClose }: { productId: string; onClose: () => void }) {
  const { tenantId } = useTenant();
  const may = usePermission();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["production-bom", tenantId, productId], queryFn: () => api.production.bom(tenantId, productId) });
  const [bom, setBom] = useState<ProductionBomDef | null>(null);
  const [names, setNames] = useState<Record<string, string>>({});
  useEffect(() => {
    if (q.data && !bom) {
      setBom(q.data.bom ?? EMPTY_BOM);
      setNames(Object.fromEntries(q.data.items.map((i) => [i.id, i.name])));
    }
  }, [q.data, bom]);
  const editable = may("production");
  const prices = !!q.data?.pricesEditable;
  const save = useMutation({
    mutationFn: () => api.production.saveBom(tenantId, productId, bom!),
    onSuccess: (r) => { toast.success(t("Stückliste gespeichert (Version {v})", { v: r.version }), r.warning ? { description: r.warning } : undefined); void qc.invalidateQueries({ queryKey: ["production-boms"] }); void qc.invalidateQueries({ queryKey: ["production-bom", tenantId, productId] }); setBom(r.bom); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const archive = useMutation({ mutationFn: () => api.production.archiveBom(tenantId, productId), onSuccess: () => { toast.success(t("Stückliste archiviert")); void qc.invalidateQueries({ queryKey: ["production-boms"] }); onClose(); }, onError: (e) => toast.error(errMsg(e)) });
  const dirty = useMemo(() => !!bom && JSON.stringify(bom) !== JSON.stringify(q.data?.bom ?? EMPTY_BOM), [bom, q.data]);
  const set = (p: Partial<ProductionBomDef>) => setBom((b) => ({ ...b!, ...p }));
  const hasComponent = !!bom?.components.some((c) => c.role === "component");

  return (
    <Sheet wide onClose={onClose}
      title={<><FileText className="size-4 text-fg-subtle" /> {t("Produktionsstückliste")}</>}
      description={q.data ? <>{q.data.product.name} <span className="font-mono text-[12px]">({q.data.product.sku})</span>{q.data.version ? ` · ${t("Version {v}", { v: q.data.version })}` : ""}</> : undefined}
      footer={editable && bom && <>
        {q.data?.version ? <Button size="sm" variant="ghost" loading={archive.isPending} onClick={() => archive.mutate()}>{t("Archivieren")}</Button> : null}
        {!hasComponent && <span className="text-[12.5px] text-fg-muted">{t("Mindestens eine Komponente (Ware) nötig")}</span>}
        <Button variant="primary" className="ml-auto" disabled={!dirty || !hasComponent} loading={save.isPending} onClick={() => save.mutate()}>{t("Speichern")}</Button>
      </>}>
      {!bom || !q.data ? <Skeleton className="h-80" /> : (
        <>
          <Section title={t("Komponenten & Kunden-Material")}>
            <div className="space-y-1.5">
              {bom.components.map((c, i) => (
                <div key={c.productId} className="flex flex-wrap items-center gap-2 rounded-xl border border-border px-3 py-2">
                  <div className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-medium">{names[c.productId] ?? c.sku}</span>
                    <span className="font-mono text-[11.5px] text-fg-muted">{c.sku}</span>
                  </div>
                  <select disabled={!editable} value={c.role} onChange={(e) => set({ components: bom.components.map((x, j) => (j === i ? { ...x, role: e.target.value as "component" | "material" } : x)) })} className="h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px]" aria-label={t("Art")}>
                    <option value="component">{t("Komponente (Ware)")}</option>
                    <option value="material">{t("Material (Karton, Flyer …)")}</option>
                  </select>
                  <Input disabled={!editable} className="w-20 text-right" value={String(c.quantity).replace(".", ",")} onChange={(e) => set({ components: bom.components.map((x, j) => (j === i ? { ...x, quantity: Number(e.target.value.replace(",", ".")) || 0 } : x)) })} aria-label={t("Menge je Durchlauf")} />
                  {editable && <button onClick={() => set({ components: bom.components.filter((_, j) => j !== i) })} className="size-8 grid place-items-center rounded-lg text-fg-subtle hover:text-danger" aria-label={t("Entfernen")}><Trash2 className="size-3.5" /></button>}
                </div>
              ))}
              {editable && <ProductSearch placeholder={t("Komponente oder Material hinzufügen (SKU, Name, EAN) …")} exclude={[productId, ...bom.components.map((c) => c.productId)]} onPick={(p) => { setNames((n) => ({ ...n, [p.id]: p.name })); set({ components: [...bom.components, { productId: p.id, sku: p.sku, quantity: 1, role: "component" }] }); }} />}
            </div>
            <label className="mt-3 flex items-center gap-2 text-[13px]">
              {t("Ergibt")}
              <Input disabled={!editable} className="w-20 text-right" inputMode="numeric" value={String(bom.outputQuantity)} onChange={(e) => set({ outputQuantity: Math.max(1, Number(e.target.value.replace(/\D/g, "")) || 1) })} />
              {t("Fertigstück(e) je Durchlauf")}
            </label>
          </Section>

          <Section title={t("Material von Logiful")} aside={t("wird berechnet")}>
            <div className="space-y-1.5">
              {bom.logifulMaterials.map((m, i) => (
                <div key={i} className="flex flex-wrap items-center gap-1.5">
                  <Input disabled={!editable} className="flex-1 min-w-40" value={m.name} onChange={(e) => set({ logifulMaterials: bom.logifulMaterials.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} aria-label={t("Material")} />
                  <Input disabled={!editable} className="w-20 text-right" value={String(m.quantity).replace(".", ",")} onChange={(e) => set({ logifulMaterials: bom.logifulMaterials.map((x, j) => (j === i ? { ...x, quantity: Number(e.target.value.replace(",", ".")) || 0 } : x)) })} aria-label={t("Menge je Stück")} />
                  <Input disabled={!editable} className="w-20" value={m.unit} onChange={(e) => set({ logifulMaterials: bom.logifulMaterials.map((x, j) => (j === i ? { ...x, unit: e.target.value } : x)) })} aria-label={t("Einheit")} />
                  <Input disabled={!prices} className="w-24 text-right" defaultValue={euros(m.priceMinor)} onBlur={(e) => set({ logifulMaterials: bom.logifulMaterials.map((x, j) => (j === i ? { ...x, priceMinor: parseEuros(e.target.value) || 0 } : x)) })} suffix="€" aria-label={t("Preis je Einheit")} />
                  {editable && <button onClick={() => set({ logifulMaterials: bom.logifulMaterials.filter((_, j) => j !== i) })} className="size-8 grid place-items-center rounded-lg text-fg-subtle hover:text-danger" aria-label={t("Entfernen")}><Trash2 className="size-3.5" /></button>}
                </div>
              ))}
              {editable && <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => set({ logifulMaterials: [...bom.logifulMaterials, { name: "", quantity: 1, unit: t("Stk."), priceMinor: 0 }] })}>{t("Material")}</Button>}
            </div>
          </Section>

          <Section title={t("Arbeitsschritte")} aside={!prices ? <span className="inline-flex items-center gap-1"><Lock className="size-3" /> {t("Preise setzt Logiful")}</span> : undefined}>
            <div className="space-y-2">
              {bom.steps.map((s, i) => {
                const upd = (p: Partial<typeof s>) => set({ steps: bom.steps.map((x, j) => (j === i ? { ...x, ...p } : x)) });
                return (
                  <div key={s.id} className="rounded-xl border border-border p-3 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="size-6 grid place-items-center rounded-full bg-surface-2 text-[12px] font-semibold">{i + 1}</span>
                      <Input disabled={!editable} className="flex-1" value={s.title} placeholder={t("z. B. Flyer einlegen")} onChange={(e) => upd({ title: e.target.value })} aria-label={t("Arbeitsschritt")} />
                      {editable && <button onClick={() => set({ steps: bom.steps.filter((_, j) => j !== i) })} className="size-8 grid place-items-center rounded-lg text-fg-subtle hover:text-danger" aria-label={t("Entfernen")}><Trash2 className="size-3.5" /></button>}
                    </div>
                    <textarea disabled={!editable} value={s.instructions} onChange={(e) => upd({ instructions: e.target.value })} rows={2} placeholder={t("Anleitung für das Team (optional)")} className="w-full rounded-lg border border-border bg-surface px-2.5 py-2 text-[13px]" />
                    <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
                      <span className="text-fg-muted">{t("Vorgabezeit")}</span>
                      <Input disabled={!editable} className="w-20 text-right" value={s.minutesPerUnit == null ? "" : String(s.minutesPerUnit).replace(".", ",")} onChange={(e) => upd({ minutesPerUnit: e.target.value.trim() ? Number(e.target.value.replace(",", ".")) || 0 : null })} aria-label={t("Minuten je Stück")} />
                      <span className="text-fg-muted">{t("Min. je Stück")}</span>
                      <span className="ml-auto text-fg-muted">{t("Preis")}</span>
                      <select disabled={!prices} value={s.priceMode} onChange={(e) => upd({ priceMode: e.target.value as typeof s.priceMode })} className="h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px]" aria-label={t("Preisart")}>
                        {PRICE_MODES.map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}
                      </select>
                      {s.priceMode !== "none" && <Input disabled={!prices} className="w-24 text-right" defaultValue={euros(s.priceMinor)} onBlur={(e) => upd({ priceMinor: parseEuros(e.target.value) || 0 })} suffix="€" aria-label={t("Preis")} />}
                    </div>
                  </div>
                );
              })}
              {editable && <Button size="sm" variant="ghost" icon={<Plus className="size-3.5" />} onClick={() => set({ steps: [...bom.steps, { id: `s${Date.now().toString(36)}`, title: "", instructions: "", minutesPerUnit: null, priceMode: "none", priceMinor: 0 }] })}>{t("Arbeitsschritt")}</Button>}
            </div>
          </Section>

          <Section title={t("Anleitungen & Fotos")}>
            <div className="flex flex-wrap items-center gap-2">
              <FileList files={q.data.files} canDelete={editable} onDeleted={() => void q.refetch()} />
              {editable && <UploadButton label={t("Datei hinzufügen")} onFile={async (f) => { await api.production.uploadBomFile(tenantId, productId, f); await q.refetch(); }} />}
            </div>
            <p className="mt-1.5 text-[12px] text-fg-subtle flex items-center gap-1"><Paperclip className="size-3" /> {t("PDF oder Bild, höchstens 15 MB – z. B. Packschema oder Foto vom fertigen Artikel.")}</p>
          </Section>

          <Section title={t("Automatisch nachproduzieren")}>
            <label className="flex items-start gap-2.5 text-[13px]">
              <input type="checkbox" disabled={!editable} className="mt-0.5 accent-ink" checked={bom.replenish.enabled} onChange={(e) => set({ replenish: { ...bom.replenish, enabled: e.target.checked } })} />
              <span>{t("Produktion automatisch beauftragen, wenn der Bestand unter den Mindestbestand fällt")}<span className="block text-[12px] text-fg-muted">{t("Connect prüft stündlich: verfügbarer Bestand + Zulauf + offene Produktion. Solange ein Auftrag offen ist, kommt kein zweiter dazu.")}</span></span>
            </label>
            {bom.replenish.enabled && (
              <div className="mt-3 grid sm:grid-cols-2 gap-3 max-w-md">
                <label className="block"><span className="text-[12.5px] font-medium">{t("Mindestbestand")}</span><Input disabled={!editable} className="mt-1" inputMode="numeric" value={String(bom.replenish.minStock)} onChange={(e) => set({ replenish: { ...bom.replenish, minStock: Math.max(1, Number(e.target.value.replace(/\D/g, "")) || 1) } })} suffix={t("Stk.")} /></label>
                <label className="block"><span className="text-[12.5px] font-medium">{t("Auffüllmenge je Auftrag")}</span><Input disabled={!editable} className="mt-1" inputMode="numeric" value={String(bom.replenish.batchQuantity)} onChange={(e) => set({ replenish: { ...bom.replenish, batchQuantity: Math.max(1, Number(e.target.value.replace(/\D/g, "")) || 1) } })} suffix={t("Stk.")} /></label>
                <label className="sm:col-span-2 flex items-start gap-2.5 text-[13px]">
                  <input type="checkbox" disabled={!prices} className="mt-0.5 accent-ink" checked={bom.replenish.autoAccept} onChange={(e) => set({ replenish: { ...bom.replenish, autoAccept: e.target.checked } })} />
                  <span className="inline-flex items-center gap-1">{!prices && <Lock className="size-3" />}{t("Von Logiful automatisch annehmen (Preis aus der Stückliste)")}</span>
                </label>
              </div>
            )}
          </Section>

          <Section title={t("Weiteres")}>
            <label className="flex items-start gap-2.5 text-[13px]">
              <input type="checkbox" disabled={!editable} className="mt-0.5 accent-ink" checked={bom.sampleRequired} onChange={(e) => set({ sampleRequired: e.target.checked })} />
              <span>{t("Muster vor der Serie freigeben")}<span className="block text-[12px] text-fg-muted">{t("Logiful fertigt erst ein Muster, schickt Fotos und produziert nach eurer Freigabe.")}</span></span>
            </label>
            <textarea disabled={!editable} value={bom.note} onChange={(e) => set({ note: e.target.value })} rows={2} placeholder={t("Hinweise zur Produktion (optional)")} className="mt-3 w-full rounded-lg border border-border bg-surface px-2.5 py-2 text-[13px]" />
          </Section>
        </>
      )}
    </Sheet>
  );
}

// ── Einstellungen (nur Logiful) ────────────────────────────────────────

function SettingsPanel() {
  const { tenantId } = useTenant();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["production-settings", tenantId], queryFn: () => api.production.settings(tenantId) });
  const [addr, setAddr] = useState<Address | null>(null);
  const [method, setMethod] = useState<string | null | undefined>(undefined);
  useEffect(() => { if (q.data && !addr) setAddr(q.data.address ?? { company: "Logiful Produktion", lastName: "", street: "", houseNumber: "", zip: "", city: "", country: "DE" }); }, [q.data, addr]);
  const save = useMutation({
    mutationFn: () => api.production.saveSettings(tenantId, { address: addr!, ...(method !== undefined ? { shippingMethodId: method } : {}) }),
    onSuccess: (d) => { qc.setQueryData(["production-settings", tenantId], d); toast.success(t("Gespeichert")); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (!q.data || !addr) return <Skeleton className="h-60" />;
  const f = (k: keyof Address, label: string, cls = "") => (
    <label className={cx("block", cls)}><span className="text-[12.5px] font-medium">{t(label)}</span><Input className="mt-1" value={(addr[k] as string | undefined) ?? ""} onChange={(e) => setAddr({ ...addr, [k]: e.target.value })} /></label>
  );
  return (
    <Card className="p-5 space-y-4 max-w-2xl">
      <div>
        <h2 className="font-semibold text-[14px]">{t("Adresse „Logiful Produktion“")}</h2>
        <p className="text-[12.5px] text-fg-muted">{t("Hierhin liefert das Lager die Komponenten beim Start einer Produktion. Gilt für alle Kunden.")}</p>
      </div>
      <div className="grid sm:grid-cols-6 gap-3">
        {f("company", t("Firma"), "sm:col-span-3")}
        {f("lastName", t("Empfänger / Abteilung"), "sm:col-span-3")}
        {f("street", t("Straße"), "sm:col-span-4")}
        {f("houseNumber", t("Nr."), "sm:col-span-2")}
        {f("zip", t("PLZ"), "sm:col-span-2")}
        {f("city", t("Ort"), "sm:col-span-3")}
        {f("country", t("Land"), "sm:col-span-1")}
      </div>
      <div>
        <h2 className="font-semibold text-[14px]">{t("Versandart für Komponenten (dieser Kunde)")}</h2>
        <ShippingMethodSelect value={method === undefined ? q.data.shippingMethodId : method} onChange={(v) => setMethod(v ?? null)} emptyLabel={t("Lager-Standard")} className="mt-1 w-72" />
      </div>
      <div className="flex justify-end"><Button variant="primary" loading={save.isPending} disabled={!addr.lastName || !addr.street || !addr.zip || !addr.city} onClick={() => save.mutate()}>{t("Speichern")}</Button></div>
    </Card>
  );
}
